/**
 * GST for an Indian store. Prices shown to customers INCLUDE GST, so a ₹100 product costs ₹100: the tax is carved out
 * of that price, never added on top. All money is in minor units (paise); rates are in basis points (1800 = 18%).
 *
 *   inside the seller's state   ->  CGST + SGST, each half of the rate
 *   to another state            ->  IGST, the full rate
 *
 * Every function here is pure and rounds in a way that keeps the pieces adding up to the whole, to the paisa.
 */

export interface IndianState {
  /** the two-digit GST state code, e.g. "09" */
  code: string;
  name: string;
  /** other spellings, and the usual abbreviations, in lower case */
  aliases: string[];
}

export const INDIAN_STATES: IndianState[] = [
  { code: '01', name: 'Jammu & Kashmir', aliases: ['jammu and kashmir', 'j&k', 'jk'] },
  { code: '02', name: 'Himachal Pradesh', aliases: ['hp'] },
  { code: '03', name: 'Punjab', aliases: ['pb'] },
  { code: '04', name: 'Chandigarh', aliases: ['ch'] },
  { code: '05', name: 'Uttarakhand', aliases: ['uttaranchal', 'uk', 'ut'] },
  { code: '06', name: 'Haryana', aliases: ['hr'] },
  { code: '07', name: 'Delhi', aliases: ['nct of delhi', 'nct delhi', 'new delhi', 'delhi ncr', 'dl'] },
  { code: '08', name: 'Rajasthan', aliases: ['rj'] },
  { code: '09', name: 'Uttar Pradesh', aliases: ['up'] },
  { code: '10', name: 'Bihar', aliases: ['br'] },
  { code: '11', name: 'Sikkim', aliases: ['sk'] },
  { code: '12', name: 'Arunachal Pradesh', aliases: ['ar'] },
  { code: '13', name: 'Nagaland', aliases: ['nl'] },
  { code: '14', name: 'Manipur', aliases: ['mn'] },
  { code: '15', name: 'Mizoram', aliases: ['mz'] },
  { code: '16', name: 'Tripura', aliases: ['tr'] },
  { code: '17', name: 'Meghalaya', aliases: ['ml'] },
  { code: '18', name: 'Assam', aliases: ['as'] },
  { code: '19', name: 'West Bengal', aliases: ['wb'] },
  { code: '20', name: 'Jharkhand', aliases: ['jh'] },
  { code: '21', name: 'Odisha', aliases: ['orissa', 'od', 'or'] },
  { code: '22', name: 'Chhattisgarh', aliases: ['chattisgarh', 'cg'] },
  { code: '23', name: 'Madhya Pradesh', aliases: ['mp'] },
  { code: '24', name: 'Gujarat', aliases: ['gj'] },
  { code: '26', name: 'Dadra & Nagar Haveli and Daman & Diu', aliases: ['dadra and nagar haveli and daman and diu', 'dadra and nagar haveli', 'daman and diu', 'daman & diu', 'dadra & nagar haveli', 'dnhdd', 'dn', 'dd'] },
  { code: '27', name: 'Maharashtra', aliases: ['mh'] },
  { code: '29', name: 'Karnataka', aliases: ['ka'] },
  { code: '30', name: 'Goa', aliases: ['ga'] },
  { code: '31', name: 'Lakshadweep', aliases: ['ld'] },
  { code: '32', name: 'Kerala', aliases: ['kl'] },
  { code: '33', name: 'Tamil Nadu', aliases: ['tamilnadu', 'tn'] },
  { code: '34', name: 'Puducherry', aliases: ['pondicherry', 'py'] },
  { code: '35', name: 'Andaman & Nicobar Islands', aliases: ['andaman and nicobar islands', 'andaman and nicobar', 'andaman & nicobar', 'an'] },
  { code: '36', name: 'Telangana', aliases: ['ts', 'tg'] },
  { code: '37', name: 'Andhra Pradesh', aliases: ['ap'] },
  { code: '38', name: 'Ladakh', aliases: ['la'] },
];

const tidy = (text: string) =>
  text
    .toLowerCase()
    .replace(/\./g, '') // "U.P." -> "up"
    .replace(/\band\b/g, '&')
    .replace(/[^a-z0-9&]+/g, ' ')
    .trim();

const STATE_INDEX = (() => {
  const index = new Map<string, IndianState>();
  for (const s of INDIAN_STATES) {
    index.set(tidy(s.name), s);
    for (const a of s.aliases) index.set(tidy(a), s);
  }
  return index;
})();

/** "uttar pradesh", "UP", "Orissa", "NCT of Delhi" -> the state; anything else -> null. */
export function resolveState(text: string | null | undefined): IndianState | null {
  if (!text) return null;
  return STATE_INDEX.get(tidy(text)) ?? null;
}

export type SupplyType = 'intra' | 'inter';

/** Same state as the seller -> CGST + SGST; anything else (including a state we cannot recognise) -> IGST. */
export function supplyType(sellerState: string | null | undefined, buyerState: string | null | undefined): SupplyType {
  const seller = resolveState(sellerState);
  const buyer = resolveState(buyerState);
  return seller && buyer && seller.code === buyer.code ? 'intra' : 'inter';
}

/** The GST contained in a price that already includes it: ₹100 at 18% holds ₹15.25 of tax (₹84.75 + ₹15.25). */
export function includedTax(gross: number, rateBps: number): number {
  if (gross <= 0 || rateBps <= 0) return 0;
  const net = Math.round((gross * 10_000) / (10_000 + rateBps));
  return gross - net;
}

export interface TaxSplit {
  cgst: number;
  sgst: number;
  igst: number;
}

/** Halves the tax into CGST and SGST (the odd paisa goes to CGST) inside the state, or all of it is IGST. */
export function splitTax(tax: number, supply: SupplyType): TaxSplit {
  if (supply === 'inter') return { cgst: 0, sgst: 0, igst: tax };
  const cgst = Math.ceil(tax / 2);
  return { cgst, sgst: tax - cgst, igst: 0 };
}

/** Shares `total` out in proportion to `weights`, in whole paise, so the shares always add up to exactly `total`. */
export function allocate(total: number, weights: number[]): number[] {
  const sum = weights.reduce((a, b) => a + b, 0);
  if (total <= 0 || sum <= 0) return weights.map(() => 0);
  const exact = weights.map((w) => (total * w) / sum);
  const shares = exact.map(Math.floor);
  let left = total - shares.reduce((a, b) => a + b, 0);
  const byRemainder = exact.map((x, i) => ({ i, r: x - Math.floor(x) })).sort((a, b) => b.r - a.r || a.i - b.i);
  for (const { i } of byRemainder) {
    if (left <= 0) break;
    shares[i]! += 1;
    left -= 1;
  }
  return shares;
}

export interface TaxLine {
  /** what the customer pays for this line, GST included, after any discount */
  gross: number;
  rateBps: number;
}

export interface TaxRow extends TaxSplit {
  rateBps: number;
  /** the price without GST */
  taxableValue: number;
  tax: number;
}

export interface TaxBreakdown {
  supply: SupplyType;
  sellerState: string | null;
  buyerState: string | null;
  /** one row per GST rate in the order, highest rate first */
  rows: TaxRow[];
  /** all the GST contained in the order */
  total: number;
  cgst: number;
  sgst: number;
  igst: number;
}

/** The GST in an order, grouped by rate, split into CGST/SGST or IGST. Shipping is just another line at its own rate. */
export function computeTax(
  lines: TaxLine[],
  opts: { sellerState: string | null; buyerState: string | null },
): TaxBreakdown {
  const supply = supplyType(opts.sellerState, opts.buyerState);
  const byRate = new Map<number, { gross: number; tax: number }>();
  for (const line of lines) {
    if (line.gross <= 0) continue;
    const row = byRate.get(line.rateBps) ?? { gross: 0, tax: 0 };
    row.gross += line.gross;
    row.tax += includedTax(line.gross, line.rateBps);
    byRate.set(line.rateBps, row);
  }
  const rows: TaxRow[] = [...byRate.entries()]
    .sort((a, b) => b[0] - a[0])
    .map(([rateBps, { gross, tax }]) => ({ rateBps, taxableValue: gross - tax, tax, ...splitTax(tax, supply) }));
  return {
    supply,
    sellerState: resolveState(opts.sellerState)?.name ?? opts.sellerState ?? null,
    buyerState: resolveState(opts.buyerState)?.name ?? opts.buyerState ?? null,
    rows,
    total: rows.reduce((s, r) => s + r.tax, 0),
    cgst: rows.reduce((s, r) => s + r.cgst, 0),
    sgst: rows.reduce((s, r) => s + r.sgst, 0),
    igst: rows.reduce((s, r) => s + r.igst, 0),
  };
}

/** "18%", "2.5%": a rate for people to read. */
export const formatRate = (rateBps: number) => `${Number((rateBps / 100).toFixed(2))}%`;

/** One price taken apart: what it is without GST, and the GST in it. */
export function priceBreakdown(gross: number, rateBps: number, supply: SupplyType = 'intra') {
  const tax = includedTax(gross, rateBps);
  return { gross, base: gross - tax, tax, ...splitTax(tax, supply) };
}

/** A GSTIN is 15 characters: state code, PAN, entity number, "Z", check character. */
export const GSTIN_PATTERN = /^\d{2}[A-Z]{5}\d{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;

export const MAX_GST_RATE_BPS = 2800;

/** An HSN code is 4, 6 or 8 digits (the longer, the more specific). */
export const HSN_PATTERN = /^\d{4}(\d{2}(\d{2})?)?$/;

/** "3406 00 10", "3406.00.10", "3406-00-10" -> "34060010". Anything left that is not an HSN code is returned as typed. */
export const normalizeHsn = (raw: string) => raw.replace(/[\s.-]/g, '');

/** A PAN is 10 characters: five letters, four digits, a letter. */
export const PAN_PATTERN = /^[A-Z]{5}\d{4}[A-Z]$/;
